import React, { useState, useRef, useEffect } from 'react';

interface Mutation {
    position: number;
    rsid: string;
    mutation: string;
    classification: "pathogenic" | "benign" | "no prediction" | "ambiguous" | string;
    ukBiobank?: string | null;
}

interface Domain {
    uniprot_id: string;
    feature_type: string;
    description: string;
    start_position: number;
    end_position: number;
}

interface SharedFilters {
    searchTerm: string;
    classificationFilter: string;
    domainFilter: string;
    ukBiobankFilter: string;
    polymorphicFilter: string;
}

interface LollipopPlotProps {
    mutations: Mutation[];
    proteinLength: number;
    uniprotId?: string;
    height?: number;
    margin?: { top: number; right: number; bottom: number; left: number };
    sharedFilters?: SharedFilters;
    setSharedFilters?: (filters: SharedFilters) => void;
    domains?: Domain[];
}

const LollipopPlot: React.FC<LollipopPlotProps> = ({
    mutations,
    proteinLength,
    uniprotId,
    height = 300,
    margin = { top: 40, right: 40, bottom: 35, left: 40 },
    sharedFilters,
    setSharedFilters,
    domains
}) => {
    const [tooltip, setTooltip] = useState<{
        visible: boolean;
        x: number;
        y: number;
        type: 'mutation' | 'domain';
        content: { rsid: string; mutation: string; classification: string; position: number };
        domainContent?: { description: string; featureType: string; start: number; end: number };
    }>({
        visible: false,
        x: 0,
        y: 0,
        type: 'mutation',
        content: { rsid: '', mutation: '', classification: '', position: 0 }
    });


    // Range selection state (protein positions, 1-based)
    const [rangeStart, setRangeStart] = useState<number>(1);
    const [rangeEnd, setRangeEnd] = useState<number>(50);
    const rangeTrackRef = useRef<HTMLDivElement>(null);
    const draggingThumb = useRef<'start' | 'end' | null>(null);

    // Local search filter (not shared)
    const [searchTerm, setSearchTerm] = useState<string>('');

    const svgRef = useRef<SVGSVGElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [svgWidth, setSvgWidth] = useState(800);

    // Domain data state
    const [domainData, setDomainData] = useState<Domain[]>([]);

    // Use domains shared from the page if provided; otherwise fetch them.
    useEffect(() => {
        if (domains) {
            setDomainData(domains);
            return;
        }
        const fetchDomainData = async () => {
            try {
                const response = await fetch('/api/domains');
                if (response.ok) {
                    const data = await response.json();
                    setDomainData(data);
                }
            } catch (error) {
                console.error('Error fetching domain data:', error);
            }
        };

        fetchDomainData();
    }, [domains]);

    // Initialize rangeEnd when proteinLength becomes available
    useEffect(() => {
        if (proteinLength > 0) {
            setRangeEnd(Math.min(50, proteinLength));
        }
    }, [proteinLength]);

    // Keep the SVG width in sync with its container. A ResizeObserver also
    // catches layout changes that aren't window resizes (e.g. the structure
    // panel expanding/collapsing), which a window 'resize' listener misses.
    useEffect(() => {
        const updateWidth = () => {
            const container = wrapperRef.current;
            if (container) {
                const containerWidth = container.clientWidth || 800;
                setSvgWidth(Math.max(400, containerWidth - 20));
            }
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        if (wrapperRef.current) observer.observe(wrapperRef.current);
        window.addEventListener('resize', updateWidth);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    // Filter and sort mutations by position
    const filteredMutations = mutations
        .filter(m => {
            // Basic position filter
            if (m.position <= 0 || m.position > proteinLength) return false;

            // Search filter (local to lollipop plot)
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                if (!(m.rsid.toLowerCase().includes(searchLower) ||
                    m.mutation.toLowerCase().includes(searchLower) ||
                    m.position.toString().includes(searchLower))) {
                    return false;
                }
            }

            // Shared Classification filter from RSID table
            if (sharedFilters?.classificationFilter && sharedFilters.classificationFilter !== 'all') {
                if (!m.classification.toLowerCase().includes(sharedFilters.classificationFilter.toLowerCase())) {
                    return false;
                }
            }

            // Shared Domain filter from RSID table
            if (sharedFilters?.domainFilter && sharedFilters.domainFilter !== 'all' && domainData.length > 0 && uniprotId) {
                const proteinDomains = domainData.filter(domain => domain.uniprot_id === uniprotId);
                let matchesDomain = false;

                if (sharedFilters.domainFilter === 'Transmembrane') {
                    matchesDomain = proteinDomains.some(domain =>
                        domain.feature_type === 'Transmembrane' &&
                        m.position >= domain.start_position &&
                        m.position <= domain.end_position
                    );
                } else if (sharedFilters.domainFilter === 'Topological domain') {
                    matchesDomain = proteinDomains.some(domain =>
                        domain.feature_type === 'Topological domain' &&
                        m.position >= domain.start_position &&
                        m.position <= domain.end_position
                    );
                } else if (sharedFilters.domainFilter === 'Outside domains') {
                    matchesDomain = !proteinDomains.some(domain =>
                        m.position >= domain.start_position &&
                        m.position <= domain.end_position
                    );
                }

                if (!matchesDomain) return false;
            }

            // Shared Biobank filter from RSID table
            if (sharedFilters?.ukBiobankFilter && sharedFilters.ukBiobankFilter !== 'all') {
                if (sharedFilters.ukBiobankFilter === 'yes' && m.ukBiobank !== 'yes') {
                    return false;
                }
                if (sharedFilters.ukBiobankFilter === 'no' && m.ukBiobank === 'yes') {
                    return false;
                }
            }

            return true;
        })
        .sort((a, b) => a.position - b.position);

    // Clamp range to protein length (position-based)
    const totalMutations = filteredMutations.length;
    const clampedStart = Math.max(1, Math.min(rangeStart, proteinLength));
    const clampedEnd = Math.max(clampedStart, Math.min(rangeEnd, proteinLength));
    const paginatedMutations = filteredMutations.filter(
        m => m.position >= clampedStart && m.position <= clampedEnd
    );

    // Group mutations by position to handle overlaps
    const mutationGroups = paginatedMutations.reduce((groups, mutation) => {
        const key = mutation.position;
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(mutation);
        return groups;
    }, {} as Record<number, Mutation[]>);

    // Reset range when filters change
    React.useEffect(() => {
        setRangeStart(1);
        setRangeEnd(Math.min(50, proteinLength || 500));
    }, [searchTerm, sharedFilters, proteinLength]);

    // Get color for classification
    const getClassificationColor = (classification: string): string => {
        const classLower = classification.toLowerCase();
        if (classLower.includes('pathogenic')) return '#ef4444'; // Red
        if (classLower.includes('benign')) return '#22c55e'; // Green
        if (classLower.includes('ambiguous')) return '#f97316'; // Orange
        return '#94a3b8'; // Gray for uncertain/no prediction
    };

    // Calculate plot dimensions
    const plotWidth = svgWidth - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const backboneY = plotHeight * 0.8;
    const maxStickHeight = plotHeight * 0.6;

    // Get min and max positions from paginated mutations for dynamic scaling
    const minPosition = paginatedMutations.length > 0
        ? Math.min(...paginatedMutations.map(m => m.position))
        : 1;
    const maxPosition = paginatedMutations.length > 0
        ? Math.max(...paginatedMutations.map(m => m.position))
        : proteinLength;
    const positionRange = maxPosition - minPosition || 1;

    // Scale for position to x coordinate (now based on visible mutation range)
    const xScale = (position: number) => ((position - minPosition) / positionRange) * plotWidth;

    // Get domains for the current protein filtered to visible range
    const visibleDomains = uniprotId
        ? domainData.filter(domain =>
            domain.uniprot_id === uniprotId &&
            domain.end_position >= minPosition &&
            domain.start_position <= maxPosition
        )
        : [];

    // Domain color mapping — keyed by description keywords
    const getDomainColor = (featureType: string, description?: string): string => {
        const desc = (description || '').toLowerCase();
        if (featureType === "Transmembrane") return 'rgba(124, 58, 237, 0.2)';   // Purple
        if (desc.includes('extracellular') || desc.includes('lumenal')) return 'rgba(59, 130, 246, 0.18)';  // Blue
        if (desc.includes('cytoplasmic') || desc.includes('intracellular')) return 'rgba(245, 158, 11, 0.18)'; // Amber
        if (desc.includes('helical')) return 'rgba(236, 72, 153, 0.18)';          // Pink
        if (featureType === "Topological domain") return 'rgba(20, 184, 166, 0.18)'; // Teal fallback
        return 'rgba(148, 163, 184, 0.12)';
    };

    const getDomainColorSolid = (featureType: string, description?: string): string => {
        const desc = (description || '').toLowerCase();
        if (featureType === "Transmembrane") return '#7c3aed';
        if (desc.includes('extracellular') || desc.includes('lumenal')) return '#3b82f6';
        if (desc.includes('cytoplasmic') || desc.includes('intracellular')) return '#f59e0b';
        if (desc.includes('helical')) return '#ec4899';
        if (featureType === "Topological domain") return '#14b8a6';
        return '#94a3b8';
    };

    // Build unique domain legend entries from visible domains
    const domainLegendEntries = (() => {
        const seen = new Set<string>();
        const entries: { label: string; color: string }[] = [];
        for (const d of visibleDomains) {
            const rawLabel = d.description || d.feature_type;
            const label = rawLabel.includes(';') ? rawLabel.split(';')[0].trim() : rawLabel;
            if (seen.has(label)) continue;
            seen.add(label);
            entries.push({ label, color: getDomainColorSolid(d.feature_type, d.description) });
        }
        return entries;
    })();

    const handleMouseEnter = (mutation: Mutation, event: React.MouseEvent) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (rect) {
            setTooltip({
                visible: true,
                x: event.clientX - rect.left,
                y: event.clientY - rect.top - 10,
                type: 'mutation',
                content: {
                    rsid: mutation.rsid,
                    mutation: mutation.mutation,
                    classification: mutation.classification,
                    position: mutation.position
                }
            });
        }
    };

    const handleDomainMouseEnter = (domain: Domain, event: React.MouseEvent) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (rect) {
            setTooltip({
                visible: true,
                x: event.clientX - rect.left,
                y: event.clientY - rect.top - 10,
                type: 'domain',
                content: { rsid: '', mutation: '', classification: '', position: 0 },
                domainContent: {
                    description: domain.description || domain.feature_type,
                    featureType: domain.feature_type,
                    start: domain.start_position,
                    end: domain.end_position,
                }
            });
        }
    };

    const handleMouseLeave = () => {
        setTooltip(prev => ({ ...prev, visible: false }));
    };

    return (
        <div className="w-100 position-relative" ref={wrapperRef}>
            {/* Control Panel */}
            <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
            }}>
                <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                    <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: '180px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input
                            id="search-mutations"
                            type="text"
                            placeholder="Search RSID, mutation, or position..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                flex: 1,
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                padding: '0.35rem 0.6rem',
                                fontSize: '0.8rem',
                                outline: 'none',
                                background: '#fff',
                                color: '#1A2940',
                                transition: 'border-color 0.15s',
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#93c5fd'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                    </div>

                    <div className="d-flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}>Pos:</span>
                        <input
                            type="number"
                            min={1}
                            max={proteinLength}
                            value={clampedStart}
                            onChange={(e) => {
                                const v = parseInt(e.target.value);
                                if (!isNaN(v) && v >= 1) {
                                    const clamped = Math.max(1, Math.min(v, proteinLength));
                                    setRangeStart(clamped);
                                    if (clamped > rangeEnd) setRangeEnd(clamped);
                                }
                            }}
                            style={{
                                width: '56px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                padding: '0.2rem 0.3rem',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#1A2940',
                                textAlign: 'center',
                                outline: 'none',
                                background: '#fff',
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#93c5fd'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                        <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>–</span>
                        <input
                            type="number"
                            min={1}
                            max={proteinLength}
                            value={clampedEnd}
                            onChange={(e) => {
                                const v = parseInt(e.target.value);
                                if (!isNaN(v) && v >= 1) {
                                    const clamped = Math.max(1, Math.min(v, proteinLength));
                                    setRangeEnd(clamped);
                                    if (clamped < rangeStart) setRangeStart(clamped);
                                }
                            }}
                            style={{
                                width: '56px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                padding: '0.2rem 0.3rem',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#1A2940',
                                textAlign: 'center',
                                outline: 'none',
                                background: '#fff',
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#93c5fd'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        />
                        <span style={{ color: '#94a3b8', fontSize: '0.7rem', marginLeft: '2px' }}>({paginatedMutations.length} mutations)</span>
                    </div>
                </div>

                {/* Range slider */}
                {proteinLength > 1 && (
                    <div style={{ padding: '0.5rem 8px 0.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 500 }}>1</span>
                            <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 500 }}>{proteinLength}</span>
                        </div>
                        <div
                            ref={rangeTrackRef}
                            style={{
                                position: 'relative',
                                height: '36px',
                                userSelect: 'none',
                                cursor: 'pointer',
                            }}
                            onMouseDown={(e) => {
                                if (!rangeTrackRef.current) return;
                                const rect = rangeTrackRef.current.getBoundingClientRect();
                                const pct = (e.clientX - rect.left) / rect.width;
                                const val = Math.max(1, Math.round(pct * proteinLength));
                                const distStart = Math.abs(val - rangeStart);
                                const distEnd = Math.abs(val - rangeEnd);
                                if (distStart < distEnd) {
                                    setRangeStart(Math.min(val, rangeEnd));
                                    draggingThumb.current = 'start';
                                } else {
                                    setRangeEnd(Math.max(val, rangeStart));
                                    draggingThumb.current = 'end';
                                }
                                document.body.style.userSelect = 'none';

                                const onMove = (ev: MouseEvent) => {
                                    const r = rangeTrackRef.current?.getBoundingClientRect();
                                    if (!r) return;
                                    const p = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
                                    const v = Math.max(1, Math.round(p * proteinLength));
                                    if (draggingThumb.current === 'start') {
                                        setRangeStart(Math.max(1, Math.min(v, rangeEnd)));
                                    } else {
                                        setRangeEnd(Math.min(proteinLength, Math.max(v, rangeStart)));
                                    }
                                };
                                const onUp = () => {
                                    draggingThumb.current = null;
                                    document.body.style.userSelect = '';
                                    document.removeEventListener('mousemove', onMove);
                                    document.removeEventListener('mouseup', onUp);
                                };
                                document.addEventListener('mousemove', onMove);
                                document.addEventListener('mouseup', onUp);
                            }}
                        >
                            {/* Track background */}
                            <div style={{
                                position: 'absolute',
                                top: '14px',
                                left: 0,
                                right: 0,
                                height: '6px',
                                background: '#e2e8f0',
                                borderRadius: '3px',
                            }} />
                            {/* Active range fill */}
                            <div style={{
                                position: 'absolute',
                                top: '14px',
                                left: `${((clampedStart - 1) / proteinLength) * 100}%`,
                                width: `${((clampedEnd - clampedStart + 1) / proteinLength) * 100}%`,
                                height: '6px',
                                background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                                borderRadius: '3px',
                                boxShadow: '0 1px 4px rgba(59,130,246,0.25)',
                            }} />
                            {/* Start thumb */}
                            <div style={{
                                position: 'absolute',
                                top: '7px',
                                left: `${((clampedStart - 1) / proteinLength) * 100}%`,
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'grab',
                            }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'linear-gradient(135deg, #fff, #f1f5f9)',
                                    border: '2.5px solid #3b82f6',
                                    borderRadius: '50%',
                                    boxShadow: '0 2px 6px rgba(59,130,246,0.3)',
                                    transition: 'box-shadow 0.15s',
                                }} />
                            </div>
                            {/* End thumb */}
                            <div style={{
                                position: 'absolute',
                                top: '7px',
                                left: `${(clampedEnd / proteinLength) * 100}%`,
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'grab',
                            }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'linear-gradient(135deg, #fff, #f1f5f9)',
                                    border: '2.5px solid #3b82f6',
                                    borderRadius: '50%',
                                    boxShadow: '0 2px 6px rgba(59,130,246,0.3)',
                                    transition: 'box-shadow 0.15s',
                                }} />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <svg
                ref={svgRef}
                width={svgWidth}
                height={height}
                style={{ maxWidth: '100%' }}
            >
                {/* Title */}
                <text
                    x={svgWidth / 2}
                    y={20}
                    textAnchor="middle"
                    style={{ fontSize: '16px', fontWeight: '600' }}
                    fill="#1A2940"
                >
                    Protein Mutation Lollipop Plot
                </text>

                {/* Plot area */}
                <g transform={`translate(${margin.left}, ${margin.top})`}>
                    {/* Domain regions as color-coded background rectangles */}
                    {visibleDomains.map((domain, index) => {
                        const startX = xScale(Math.max(domain.start_position, minPosition));
                        const endX = xScale(Math.min(domain.end_position, maxPosition));
                        const width = endX - startX;
                        const solidColor = getDomainColorSolid(domain.feature_type, domain.description);

                        return (
                            <g key={`domain-${index}`}>
                                <rect
                                    x={startX}
                                    y={0}
                                    width={width}
                                    height={backboneY}
                                    fill={getDomainColor(domain.feature_type, domain.description)}
                                    stroke={solidColor}
                                    strokeWidth="1"
                                    strokeOpacity={0.4}
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(e) => handleDomainMouseEnter(domain, e)}
                                    onMouseMove={(e) => handleDomainMouseEnter(domain, e)}
                                    onMouseLeave={handleMouseLeave}
                                />
                            </g>
                        );
                    })}

                    {/* Protein backbone line */}
                    <line
                        x1={0}
                        y1={backboneY}
                        x2={plotWidth}
                        y2={backboneY}
                        stroke="#4b5563"
                        strokeWidth="3"
                    />

                    {/* Position markers - dynamically spaced based on visible range */}
                    {(() => {
                        const tickCount = 5; // Number of tick marks to show
                        const step = Math.ceil(positionRange / (tickCount - 1));
                        return Array.from({ length: tickCount }, (_, i) => {
                            const position = minPosition + (i * step);
                            if (position <= maxPosition) {
                                const x = xScale(position);
                                return (
                                    <g key={position}>
                                        <line
                                            x1={x}
                                            y1={backboneY - 5}
                                            x2={x}
                                            y2={backboneY + 5}
                                            stroke="#6b7280"
                                            strokeWidth="1"
                                        />
                                        <text
                                            x={x}
                                            y={backboneY + 20}
                                            textAnchor="middle"
                                            fontSize="10"
                                            fill="#6b7280"
                                        >
                                            {position}
                                        </text>
                                    </g>
                                );
                            }
                            return null;
                        });
                    })()}

                    {/* Start and end labels */}
                    <text x={0} y={backboneY + 20} textAnchor="middle" fontSize="10" fill="#6b7280">
                        {minPosition}
                    </text>
                    <text x={plotWidth} y={backboneY + 20} textAnchor="middle" fontSize="10" fill="#6b7280">
                        {maxPosition}
                    </text>

                    {/* Mutation lollipops */}
                    {Object.entries(mutationGroups).map(([position, mutations]) => {
                        const pos = parseInt(position);
                        const baseX = xScale(pos);
                        const circleRadius = 4;
                        const stemSpacing = circleRadius * 2 + 3; // space between each lollipop head

                        return mutations.map((mutation, index) => {
                            // Each mutation gets its own stem from the same base point,
                            // with increasing heights so they're all visible
                            const stickHeight = maxStickHeight * 0.45 + index * stemSpacing;
                            const clampedHeight = Math.min(stickHeight, maxStickHeight);
                            const circleY = backboneY - clampedHeight;
                            const color = getClassificationColor(mutation.classification);

                            return (
                                <g key={`${mutation.rsid}-${mutation.mutation}-${index}`}>
                                    {/* Stick - all originate from the same backbone point */}
                                    <line
                                        x1={baseX}
                                        y1={backboneY}
                                        x2={baseX}
                                        y2={circleY + circleRadius}
                                        stroke="#6b7280"
                                        strokeWidth="2"
                                    />

                                    {/* Circle */}
                                    <circle
                                        cx={baseX}
                                        cy={circleY}
                                        r={circleRadius}
                                        fill={color}
                                        stroke="white"
                                        strokeWidth="2"
                                        style={{ cursor: 'pointer' }}
                                        onMouseEnter={(e) => handleMouseEnter(mutation, e)}
                                        onMouseLeave={handleMouseLeave}
                                    />
                                </g>
                            );
                        });
                    })}
                </g>

                {/* Position Range Info - Bottom Right */}
                <text
                    x={svgWidth - margin.right - 10}
                    y={height - 10}
                    textAnchor="end"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    fill="#6b7280"
                >
                    Position Range: {minPosition}–{maxPosition}
                </text>
            </svg>

            {/* Legends */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem 1.5rem',
                padding: '0.5rem 0.25rem',
                fontSize: '0.7rem',
                color: '#374151',
            }}>
                {/* Classification legend */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.7rem' }}>Classification:</span>
                    {[
                        { label: 'Pathogenic', color: '#ef4444' },
                        { label: 'Benign', color: '#22c55e' },
                        { label: 'Ambiguous', color: '#f97316' },
                        { label: 'Uncertain', color: '#94a3b8' },
                    ].map(item => (
                        <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                            {item.label}
                        </span>
                    ))}
                </div>

                {/* Domain legend */}
                {domainLegendEntries.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.7rem' }}>Domains:</span>
                        {domainLegendEntries.map(item => (
                            <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '2px',
                                    background: item.color,
                                    opacity: 0.35,
                                    border: `1.5px solid ${item.color}`,
                                    flexShrink: 0,
                                }} />
                                {item.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Tooltip */}
            {tooltip.visible && (
                <div
                    className="position-absolute bg-dark text-white p-2 rounded shadow-lg"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translate(-50%, -100%)',
                        fontSize: '12px',
                        zIndex: 1000,
                        whiteSpace: 'nowrap'
                    }}
                >
                    {tooltip.type === 'mutation' ? (
                        <>
                            <div><strong>RSID:</strong> {tooltip.content.rsid}</div>
                            <div><strong>Mutation:</strong> {tooltip.content.mutation}</div>
                            <div><strong>Position:</strong> {tooltip.content.position}</div>
                            <div><strong>Classification:</strong> {tooltip.content.classification}</div>
                        </>
                    ) : tooltip.domainContent && (
                        <>
                            <div><strong>{tooltip.domainContent.description}</strong></div>
                            <div style={{ color: '#94a3b8', fontSize: '11px' }}>{tooltip.domainContent.featureType}</div>
                            <div>Position {tooltip.domainContent.start}–{tooltip.domainContent.end}</div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default LollipopPlot;